import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import styles from "styles.module.css";
import {
	Grid,
	GridCellProps,
	GridColumn,
	GridCustomFooterCellProps,
} from "@progress/kendo-react-grid";
import { quotaTransactionGridColumnItem } from "./GridColumns";
import { getQuotaTransactionApi } from "api/vessel";
import { IQuotaTransaction } from "interface/vessel";
import { formatNorwegianNumber } from "utils/helper";
import OverLayLoading from "ui/Loading";
import GridCustomPage from "ui/grid/GridPage";
import { GridPDFExport } from "@progress/kendo-react-pdf";
import { ExcelExport } from "@progress/kendo-react-excel-export";
import { COLOR, ICON_NAMES } from "utils/constant";
import GridWithGroupPaging from "ui/grid/GridWithGroupPaging";
import { PageTypePath } from "utils/pages";
import Filter from "./Filter";

const QuotaTransactionPage: React.FC<{}> = ({}) => {
	const { t } = useTranslation();
	const location = useLocation();

	const overlayRef = useRef<HTMLDivElement>(null);

	const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
	const [data, setData] = useState<IQuotaTransaction[]>([]);

	const pdfRef = useRef<GridPDFExport>(null);
	const excelRef = useRef<ExcelExport>(null);

	const exportGridAsPDF = () => {
		//console.log('her', reportGuid);

		if (reportGuid && reportGuid !== '') {
			window.open(`/${PageTypePath.Pdf}/${reportGuid}`, "_blank");
		}
		return;
	};

	const exportGridAsExcel = () => {
		if (excelRef.current !== null) {
			excelRef.current.save();
		}
	};

	const params = new URLSearchParams(location.search);
	const accountNumber = params.get("accountNumber") ?? "";
	const validFrom = params.get("validFrom") ?? "";
	const validTo = params.get("validTo") ?? "";
	const source = params.get("source") ?? "1";
	const reportGuid = params.get("rg") ?? "";

	useEffect(() => {
		if (!accountNumber || !validFrom) return;

		getQuotaTransactionApi(accountNumber, validFrom, validTo, source)
			.then((result) => setData(result ?? []))
			.catch(() => {
				// setError(t("ErrorLoadingData"));
			});
		// .finally(() => setLoading(false));
	}, [accountNumber, validFrom, validTo, source]);

	useEffect(() => {
		const updateMobileView = () => setIsMobileView(window.innerWidth < 768);
		window.addEventListener("resize", updateMobileView);

		return () => {
			window.removeEventListener("resize", updateMobileView);
		};
	}, []);

	const FooterCustomCell = (props: GridCustomFooterCellProps) => {
		const values = data.length > 0 ? data[0] : {};

		const handleNumber = (value: number | undefined) => {
			if (value === undefined) return "";
			const minDecimal = 2;
			return formatNorwegianNumber(value, minDecimal, minDecimal);
		};
		if (props.field === "amount") {
			return (
				<td
					colSpan={props.colSpan}
					style={{ ...props.style, textAlign: "right" }}
				>
					<div>
						<span style={{ fontWeight: "bold", display: "inline-block" }}>
							Total Fangst :{" "}
						</span>
						<span style={{ fontWeight: "bold", display: "inline-block" }}>
							Herav direkte :{" "}
						</span>
						<span style={{ fontWeight: "bold", display: "inline-block" }}>
							Herav bifangst :{" "}
						</span>
					</div>
				</td>
			);
		} else if (props.field === "catchAmountToDate") {
			return (
				<td
					colSpan={props.colSpan}
					style={{ ...props.style }}
					{...props.tdProps}
				>
					<div
						style={{
							...props.style,
							color: COLOR.TextLight,
							textAlign: "right",
						}}
					>
						<span style={{ display: "block", ...props.style }}>
							{handleNumber(values.catchAmountToDateSummary)}
						</span>
						<span style={{ display: "block", ...props.style }}>
							{handleNumber(values.catchAmountToDateD)}
						</span>
						<span style={{ display: "block", ...props.style }}>
							{handleNumber(values.catchAmountToDateB)}
						</span>
					</div>
				</td>
			);
		} else if (props.field === "balanceTypeText") {
			return (
				<td
					colSpan={3}
					style={{
						overflow: "visible",
						whiteSpace: "normal",
						wordBreak: "break-word",
					}}
				>
					{values.remainingQuotaText && (
						<div style={{ fontWeight: "bold" }}>
							{values.remainingQuotaText}
						</div>
					)}
					{values.catchAndReimbursementSummaryText && (
						<div style={{ color: COLOR.TextLight, marginTop: 4 }}>
							{values.catchAndReimbursementSummaryText}
						</div>
					)}
					{values.illegalFishingText && (
						<div style={{ color: COLOR.TextLight, marginTop: 4 }}>
							{values.illegalFishingText}
						</div>
					)}
					{values.illegalFishingText && (
						<div style={{ color: COLOR.TextLight, marginTop: 4 }}>
							{values.illegalFishingText}
						</div>
					)}
					{values.summaryText && (
						<div style={{ color: COLOR.TextLight, marginTop: 4 }}>
							{values.summaryText}
						</div>
					)}
				</td>
			);
		} else if (
			props.field === "documentNumber" ||
			props.field === "catchType"
		) {
			return <td style={{ display: "none" }} />;
		} else {
			return <td {...props.tdProps}></td>;
		}
	};

	return (
		<div className={`${styles.PageContainer} released-package-page`}>
			<div ref={overlayRef} style={{ display: "none" }}>
				<OverLayLoading />
			</div>
			<div className="page-header">
				<legend className={styles.BoxHeadingLarge}>
					{t("QuotaTransactions")}
				</legend>
			</div>
			<div>
				<Filter
					exportAsPDF={exportGridAsPDF}
					exportAsExcel={exportGridAsExcel}
				/>
			</div>
			<div style={{ marginTop: 30, height: "100%" }}>
				<GridWithGroupPaging
					gridColumns={quotaTransactionGridColumnItem()}
					pageable={false}
					gridData={data}
					gridStyle={{
						height: "auto",
					}}
					// groupable={{ footer: "visible", enabled: false }}
					// className="opened-statistics-grid"
					exportGridAsPDFRef={ pdfRef }
					exportGridAsExcelRef={excelRef}
					cells={{
						footerCell: FooterCustomCell,
					}}
					excelProps={{
						fileName: `quota_transaction_${new Date().toISOString()}.xlsx`,
					}}
					
				/>
			</div>
		</div>
	);
};

export default QuotaTransactionPage;
